import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@commons/header";
import Row from "@components/row";
import Table from "@components/table";
import SubscriptionCard from "@commons/cards/subscription";
import Svg from "@components/svg";
import { eye, switchOn, switchOff } from "@assets/icons";
import { useSelector, useDispatch } from "react-redux";
import { Actions, RootState } from "@store/index";
import { User, Chat } from "@apis/index";
import useConfig from "@hooks/useConfig";
import { toast } from "react-toastify";
import usePermission from "@hooks/usePermission";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import { Helper } from "@utils/index";
import { MessageSquare, Eye, Wallet } from "lucide-react";
import theme from "@utils/theme";
import { useModal } from "@context/ModalContext";
import WalletAdjustmentModal from "@components/modals/WalletAdjustmentModal";
import { useQueryState } from "@hooks/useQueryState";

const CustomerListTable = () => {
  const { bucketUrl } = useConfig();
  const navigate = useNavigate();
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");
  const { hasPermission } = usePermission();
  const { settings: currencySettings } = useGeneralSettings();
  const dispatch = useDispatch();

  const {
    items: customers = [],
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.customers);

  const { showModal, hideModal } = useModal();

  const fetchCustomers = useCallback(async () => {
    await User.list({
      state: "customers",
      params: { role: "customer", page, limit: 10, search },
    });
  }, [page, search]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setPage(1);
  }, [search]);

  const customerStats = [
    {
      count: totalItems?.toString() || "0",
      title: "Total Customers",
      icon: "/pngs/subscription/subscribed-user.png",
      status: "subscribed",
    },
    {
      count: customers.filter((c: any) => c.isActive).length.toString(),
      title: "Active Customers",
      icon: "/pngs/subscription/active-user.png",
      status: "active",
    },
    {
      count: customers.filter((c: any) => !c.isActive).length.toString(),
      title: "Inactive Customers",
      icon: "/pngs/subscription/expired-user.png",
      status: "expired",
    },
  ];

  const columns = [
    {
      title: "Customer Info",
      key: "customerInfo",
      width: "25%",
      render: (item: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              position: "relative",
              width: "48px",
              height: "48px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                backgroundColor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontWeight: 600,
                fontSize: "18px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                border: "1px solid #e2e8f0",
                textTransform: "uppercase",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 1,
              }}
            >
              {item.name?.charAt(0) || "U"}
            </div>
            {item.avatar && (
              <img
                src={`${bucketUrl}/${item.avatar}`}
                alt={item.name}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "12px",
                  objectFit: "cover",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  border: "1px solid #f1f5f9",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 2,
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <span
              style={{ fontWeight: 600, fontSize: "15px", color: "#0f172a" }}
            >
              {item.name}
            </span>
            <span style={{ color: "#64748b", fontSize: "13px" }}>
              {item.phoneNo}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      key: "email",
      width: "20%",
      render: (item: any) => (
        <span style={{ color: "#475569", fontSize: "14px" }}>
          {item.email || "No Email Provided"}
        </span>
      ),
    },
    {
      title: "Activity",
      key: "activity",
      width: "20%",
      render: (item: any) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                color: "#64748b",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Orders
            </span>
            <span
              style={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}
            >
              {item.totalOrders || 0}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                color: "#64748b",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Wallet
            </span>
            <span
              style={{ fontWeight: 600, color: "#10b981", fontSize: "14px" }}
            >
              {Helper.formatCurrency(item.walletBalance || 0, currencySettings)}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "15%",
      render: (item: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Svg
            src={item.isActive ? switchOn : switchOff}
            onClick={async (e) => {
              e.stopPropagation();
              if (!hasPermission("customers", "Edit Details")) {
                toast.error("You don't have permission to edit customers");
                return;
              }
              try {
                const targetActive = !item.isActive;
                await toast.promise(
                  User.update({
                    state: "customers",
                    id: item.id,
                    data: { isActive: targetActive, role: "customer" },
                  }),
                  {
                    pending: "Updating customer status...",
                    success: "Customer status updated successfully!",
                    error: "Failed to update customer status.",
                  }
                );
                dispatch(Actions.update("customers", { id: item.id, isActive: targetActive }));
              } catch (err) {
                console.error("Failed to update customer status:", err);
              }
            }}
          />
          <span
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: item.isActive ? "#10b981" : "#ef4444",
              backgroundColor: item.isActive ? "#d1fae5" : "#fee2e2",
              padding: "2px 8px",
              borderRadius: "12px",
            }}
          >
            {item.isActive ? "Active" : "Inactive"}
          </span>
        </div>
      ),
    },
    {
      title: "Joined",
      key: "joinedDate",
      width: "10%",
      render: (item: any) => (
        <span style={{ color: "#64748b", fontSize: "14px" }}>
          {new Date(item.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </span>
      ),
    },
  ];

  const actions = [
    {
      key: "view",
      icon: eye,
      permission: "View List",
      title: "View Details",
      onClick: (item: any) =>
        navigate(`/admin/users/customer/customer-lists/view/${item.id}`, {
          state: item,
        }),
      style: {
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        padding: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s",
      },
    },

    {
      key: "wallet",
      icon: <Wallet size={16} color={theme.colors.green500} />,
      permission: "Manage Wallet",
      title: "Adjust Wallet",
      onClick: (item: any, e: any) => {
        e.stopPropagation();
        showModal(
          <WalletAdjustmentModal
            user={item}
            onClose={hideModal}
            onSuccess={fetchCustomers}
            currencySettings={currencySettings}
          />,
        );
      },
      style: {
        background: "#f0fdf4",
        border: "1px solid #bbf7d0",
        borderRadius: "8px",
        padding: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s",
      },
    },
  ].filter((action) => hasPermission("customers", action.permission));

  return (
    <div>
      <Header title="Customer List" />
      <br />
      <Row>
        {customerStats.map((item, i) => (
          <SubscriptionCard key={i} item={item} />
        ))}
      </Row>
      <br />
      <Table
        data={customers}
        columns={columns}
        actions={actions}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={10}
        pageControlled={page}
        onPageChange={(p: number) => setPage(p)}
        hasSearchbar={true}
        onSearchChange={setSearch}
        onRowClick={(item: any) =>
          navigate(`/admin/users/customer/customer-lists/view/${item.id}`, {
            state: item,
          })
        }
      />
    </div>
  );
};

export default CustomerListTable;
